import { Flame, Ban, Zap, Hourglass, Lightbulb, Clock, CalendarCheck, X, UserRound } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { FLAGS, TAG_COLOURS, OWNER_COLOURS, daysSince, initials, reviewState, daysUntil } from './lib';
import { useBiz } from './BizContext';

const FLAG_ICONS = { urgent: Flame, blocked: Ban, quick_win: Zap, waiting: Hourglass, idea: Lightbulb };

// British dates by default (24/11/2026); month-only dates read "Dec 2026" so they can't be mistaken for the 26th.
export const fmtDate = (s, f = 'dd/MM/yyyy') => { try { return s ? format(parseISO(String(s).slice(0, 10)), f) : '—'; } catch { return '—'; } };
export const fmtMonth = (s) => fmtDate(s, 'MMM yyyy');

export function Tag({ k, tag }) {
  const { tagMap } = useBiz();
  const t = tag || tagMap[k];
  if (!t) return null;
  const c = TAG_COLOURS[t.color] || TAG_COLOURS.paper;
  return <span className="biz-chip" style={{ background: c.bg, color: c.fg }} data-testid={`biz-tag-${t.key}`}>{t.label}</span>;
}

export function FlagChip({ k }) {
  const f = FLAGS[k];
  const Icon = FLAG_ICONS[k];
  if (!f) return null;
  return (
    <span className="biz-chip" style={{ background: f.bg, color: f.fg }} data-testid={`biz-flag-${k}`}>
      <Icon className="w-3 h-3" /> {f.label}
    </span>
  );
}

const ownerList = (e) => (e?.owners?.length ? e.owners : e?.owner_id ? [{ id: e.owner_id, name: e.owner_name }] : []);
export const ownerNames = (e) => ownerList(e).map((o) => o.name).filter(Boolean).join(', ');

// Overlapping initials for every owner of a goal/initiative (lead first); "?" when nobody owns it.
export function AvatarStack({ entity, size = 24, max = 3 }) {
  const list = ownerList(entity);
  if (!list.length) return <Avatar size={size} />;
  const shown = list.slice(0, max); const extra = list.length - shown.length;
  return (
    <span className="inline-flex items-center" title={ownerNames(entity)} data-testid="biz-owners">
      {shown.map((o, i) => <span key={o.id} className="inline-flex rounded-full" style={{ marginLeft: i ? -size * 0.3 : 0, boxShadow: '0 0 0 2px var(--paper-2)', zIndex: shown.length - i, position: 'relative' }}><Avatar ownerId={o.id} name={o.name} size={size} /></span>)}
      {extra > 0 && <span className="inline-flex items-center justify-center rounded-full font-semibold" style={{ width: size, height: size, fontSize: Math.max(12, Math.round(size * 0.4)), marginLeft: -size * 0.3, background: 'var(--paper-3)', color: 'var(--ink-2)', boxShadow: '0 0 0 2px var(--paper-2)', position: 'relative' }}>+{extra}</span>}
    </span>
  );
}

// Multi-select of admins: chips for the chosen ones plus an "Add" dropdown of the rest.
export function OwnersPicker({ value = [], onChange, testId = 'biz-owners-picker' }) {
  const { owners } = useBiz();
  const chosen = value.map((id) => owners.find((o) => o.id === id)).filter(Boolean);
  const rest = owners.filter((o) => !value.includes(o.id));
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
      {chosen.map((o, i) => (
        <span key={o.id} className="inline-flex items-center gap-1 rounded-full pl-0.5 pr-1.5 py-0.5 text-xs font-medium" style={{ background: 'var(--paper-3)' }} data-testid={`${testId}-chip-${o.id}`}>
          <Avatar ownerId={o.id} name={o.name} size={18} /> {o.name}{i === 0 && chosen.length > 1 && <span className="text-xs" style={{ color: 'var(--ink-3)' }}>lead</span>}
          <button type="button" onClick={(e) => { e.preventDefault(); onChange(value.filter((id) => id !== o.id)); }} className="ml-0.5 rounded-full hover:bg-black/10 p-0.5" aria-label={`Remove ${o.name}`} data-testid={`${testId}-remove-${o.id}`}><X className="w-3 h-3" /></button>
        </span>
      ))}
      {rest.length > 0 && (
        <select value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])} className="biz-input text-xs py-1 w-auto" style={{ minWidth: 120 }} data-testid={`${testId}-add`}>
          <option value="">{chosen.length ? '+ Add owner' : 'Nobody yet — add…'}</option>
          {rest.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
    </div>
  );
}

export function Avatar({ ownerId, name, size = 24 }) {
  const { owners } = useBiz();
  const idx = owners.findIndex((o) => o.id === ownerId);
  const o = owners[idx];
  const label = o?.name || name;
  if (!label) {
    return <span title="Assign an owner" aria-label="Unassigned — assign an owner" className="inline-flex items-center justify-center rounded-full shrink-0" style={{ width: size, height: size, border: '1px solid var(--line-2)', color: 'var(--ink-3)', opacity: 0.85 }} data-testid="biz-avatar-unassigned"><UserRound style={{ width: size * 0.55, height: size * 0.55 }} strokeWidth={1.75} /></span>;
  }
  const c = TAG_COLOURS[OWNER_COLOURS[(idx >= 0 ? idx : 0) % OWNER_COLOURS.length]];
  return (
    <span title={label} className="inline-flex items-center justify-center rounded-full font-semibold shrink-0" data-testid={`biz-avatar-${ownerId || 'none'}`}
      style={{ width: size, height: size, fontSize: Math.max(12, Math.round(size * 0.42)), background: c.bg, color: c.fg, border: '1px solid var(--line)' }}>
      {initials(label)}
    </span>
  );
}

function Dots({ n, color }) {
  return (
    <span className="inline-flex gap-[3px] items-center">
      {[1, 2, 3].map((i) => <span key={i} className="w-[6px] h-[6px] rounded-full" style={{ background: i <= n ? color : 'var(--paper-3)' }} />)}
    </span>
  );
}

export function ImpactEffort({ impact, effort }) {
  return (
    <span className="inline-flex items-center gap-3 text-xs" style={{ color: 'var(--ink-3)' }}>
      <span className="inline-flex items-center gap-1.5">Impact <Dots n={impact} color="var(--sage-deep)" /></span>
      <span className="inline-flex items-center gap-1.5">Effort <Dots n={effort} color="var(--apricot-deep)" /></span>
    </span>
  );
}

export function StaleChip({ item }) {
  const n = daysSince(item.updated_at);
  if (item.status === 'done' || n < 30) return null;
  return (
    <span className="biz-chip" style={{ background: 'var(--paper-3)', color: 'var(--ink-2)' }} data-testid={`biz-stale-${item.id}`}>
      <Clock className="w-3 h-3" /> Not reviewed in {Math.round(n / 7)} wks
    </span>
  );
}

export function ReviewChip({ goal }) {
  const st = reviewState(goal);
  if (!st) return null;
  const n = daysUntil(goal.review_date);
  const tone = st === 'overdue' ? { bg: 'var(--rose-soft)', fg: 'var(--rose-deep)' } : st === 'due' ? { bg: 'var(--apricot-soft)', fg: 'var(--apricot-deep)' } : { bg: 'var(--paper-3)', fg: 'var(--ink-2)' };
  const text = st === 'overdue' ? `Review ${-n}d overdue` : st === 'due' ? (n === 0 ? 'Review today' : `Review in ${n}d`) : `Review ${fmtDate(goal.review_date)}`;
  return <span className="biz-chip" style={{ background: tone.bg, color: tone.fg }} data-testid={`biz-review-${goal.id}`}><CalendarCheck className="w-3 h-3" /> {text}</span>;
}

export function Eyebrow({ children, className = '' }) {
  return <p className={`text-xs font-semibold uppercase tracking-[0.12em] ${className}`} style={{ color: 'var(--ink-3)' }}>{children}</p>;
}

// tone: 'muted' (zero) · 'amber' (needs a look) · 'green' (good) · 'red' (slipped) · undefined (plain ink)
const TONES = { muted: 'var(--ink-3)', amber: 'var(--honey-deep)', green: 'var(--sage-deep)', red: 'var(--rose-deep)' };
export function StatTile({ label, value, hint, tone, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} className="biz-card biz-card-hover text-left px-5 py-4 w-full min-w-0" data-testid={testId}>
      <div className="min-h-[20px]"><Eyebrow>{label}</Eyebrow></div>
      <div className="flex items-end gap-2 mt-2">
        <span className="biz-display text-4xl leading-none" style={{ color: TONES[tone] || 'var(--ink)' }} data-tone={tone || 'default'} data-testid={testId ? `${testId}-value` : undefined}>{value}</span>
        {hint && <span className="text-xs pb-1" style={{ color: 'var(--ink-3)' }}>{hint}</span>}
      </div>
    </button>
  );
}

export function Progress({ pct, color }) {
  return <div className="biz-bar"><span style={{ width: `${pct}%`, background: color }} /></div>;
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--ink-3)' }}>{label}</span>
      {children}
      {hint && <span className="text-xs block mt-1" style={{ color: 'var(--ink-3)' }}>{hint}</span>}
    </label>
  );
}

export function Seg({ options, value, onChange, testId, color }) {
  return (
    <div className="biz-seg" data-testid={testId}>
      {options.map((o) => (
        <button key={o.key} type="button" data-active={value === o.key} onClick={() => onChange(o.key)} data-testid={`${testId}-${o.key}`} style={value === o.key && color ? { color } : undefined}>{o.label}</button>
      ))}
    </div>
  );
}
